import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from "react";
import classnames from "classnames";
import { ProjectDocumentTreeDocumentRowChrome } from "@sudowrite/shared-chat";
import { DocumentListActionMenu } from "../DocumentListActionMenu";
import { AnimateTyping } from "../AnimateTyping";
import usePreviousDistinct from "../../../hooks/usePreviousDistinct";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { useProjectStore } from "../../../stores/projectStore";
import { useAppStore } from "../../../stores/appStore";
import { useDraggable } from "@dnd-kit/core";
import { MAX_DOCUMENT_TITLE_LENGTH } from "../../../../../src/shared/constants";
import { useCommentStore } from "../../../stores/commentStore";
import { useToggleCommentMode } from "../../../hooks/useToggleCommentMode";
import { useGetLinkedChapter } from "src/hooks/useGetLinkedDocuments";

export const SortableDocument = ({
  item,
  newTitle,
  setNewTitle,
  updateTitle,
  updateDocumentTitle,
  inputRef,
  nestingLevel,
  isSelected,
  ariaSelected,
  onSelect,
  onKeyboardMove,
}) => {
  const editingTitle = useProjectStore((state) => state.editingTitle);
  const setEditingTitle = useProjectStore((state) => state.setEditingTitle);

  const title = item.title?.length > 0 ? item.title : "Untitled";
  const untitled = !item.title || item.title.length === 0;

  const draggableData = useMemo(() => ({ nodeType: "document" }), []);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: item.id,
    data: draggableData,
  });

  const setDraggableRefs = useCallback(
    (node) => {
      setNodeRef(node);
      setActivatorNodeRef(node);
    },
    [setNodeRef, setActivatorNodeRef],
  );

  const isEditing = !isDragging && editingTitle === item.id;

  // Rendered inside the shared row so renaming keeps the row's indent and icon.
  const editingContent = (
    <form
      className="document-list-item-editing"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault();
        updateDocumentTitle(editingTitle, inputRef.current?.value ?? newTitle);
      }}
    >
      <label className="sr-only" htmlFor={`document-title-${item.id}`}>
        Document title
      </label>
      <input
        id={`document-title-${item.id}`}
        maxLength={MAX_DOCUMENT_TITLE_LENGTH}
        autoFocus
        ref={inputRef}
        type="text"
        defaultValue={newTitle}
        onBlur={(e) => {
          updateDocumentTitle(editingTitle, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setEditingTitle(null);
          }
          e.stopPropagation();
        }}
      />
    </form>
  );

  return (
    <div className="tree-display-document">
      <DocumentListItem
        key={item.id}
        title={title}
        untitled={untitled}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        updateTitle={updateTitle}
        isDragging={isDragging}
        item={item}
        nestingLevel={nestingLevel}
        isSelected={isSelected}
        ariaSelected={ariaSelected}
        onSelect={onSelect}
        onKeyboardMove={onKeyboardMove}
        rowRef={setDraggableRefs}
        dragListeners={listeners}
        dragAttributes={attributes}
        isEditing={isEditing}
        editingContent={editingContent}
      />
    </div>
  );
};

export const DocumentListItem = memo(function DocumentListItem({
  idx: _idx,
  item,
  newTitle,
  setNewTitle,
  title,
  total: _total,
  untitled,
  updateTitle,
  isDragging,
  nestingLevel = 0,
  isSelected = false,
  ariaSelected = false,
  onSelect,
  onKeyboardMove,
  rowRef,
  dragListeners,
  dragAttributes,
  isEditing = false,
  editingContent,
}) {
  const setDocumentId = useProjectStore((state) => state.setDocumentId);
  const selectedDocumentId = useProjectStore((state) => state.selectedDocumentId);
  const loadedDocumentId = useProjectStore((state) => state.document?.id);
  const setEditingTitle = useProjectStore((state) => state.setEditingTitle);
  const editingTitle = useProjectStore((state) => state.editingTitle);
  const setCommentsLoaded = useCommentStore((state) => state.setCommentsLoaded);
  const { toggleComments } = useToggleCommentMode();
  const linkedChapter = useGetLinkedChapter(item.id);

  const active = (selectedDocumentId ?? loadedDocumentId) === item.id;
  const previousTitle = usePreviousDistinct(title);
  const [doneAnimating, setDoneAnimating] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Match Tamagui's primary-input hover gate, including hybrid touchscreen laptops.
  const canHover = useMediaQuery("(hover: hover)");
  const documentRef = useRef(null);

  const isSW2 = useAppStore((state) => state.isSW2);
  const projectId = useProjectStore((state) => state.project?.id);

  useEffect(() => {
    if (active && documentRef.current) {
      documentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [active]);

  const handleDocumentClick = (e) => {
    updateTitle(editingTitle, newTitle);

    if (e.button === 1) {
      return;
    }

    onSelect?.(e, item.id);

    if (e.shiftKey) {
      e.preventDefault();
      return;
    }

    if (e.metaKey || e.ctrlKey) {
      return;
    }

    e.preventDefault();
    setCommentsLoaded(false);
    toggleComments(false);
    setDocumentId(item.id, null, item.id === loadedDocumentId, linkedChapter);
  };

  const setRowRef = useCallback(
    (node) => {
      documentRef.current = node;
      rowRef?.(node);
    },
    [rowRef],
  );

  return (
    <ProjectDocumentTreeDocumentRowChrome
      name={title}
      depth={nestingLevel}
      controlsVisibility={canHover ? "hover" : "always"}
      isActive={active}
      isSelected={isSelected}
      isAriaSelected={ariaSelected}
      isUntitled={untitled}
      showDocumentIcon={isSW2}
      dataNodeId={item.id}
      rowRef={setRowRef}
      tabIndex={isEditing ? -1 : 0}
      opacity={isDragging ? 0.5 : 1}
      actionsVisible={isMenuOpen}
      isEditing={isEditing}
      editingContent={editingContent}
      className={classnames("project-tree-document-row", {
        "is-active": active,
        "is-selected": isSelected,
        "no-highlight": isDragging !== null && isDragging !== item.id,
      })}
      dragListeners={isEditing ? undefined : dragListeners}
      dragAttributes={isEditing ? undefined : dragAttributes}
      onClick={isEditing ? undefined : handleDocumentClick}
      onDoubleClick={(e) => {
        e.preventDefault();
        setEditingTitle(item.id);
        setNewTitle(item.title ?? "");
      }}
      onKeyDown={isEditing ? undefined : (e) => {
        if (e.altKey && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          const directionMap = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right" };
          onKeyboardMove?.(item.id, directionMap[e.key]);
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          handleDocumentClick(e);
        }
      }}
      titleContent={
        <a
          className={classnames("document-label use-component-styles", {
            untitled,
            active,
          })}
          href={`/app/project/${projectId}#doc=${item.id}`}
          tabIndex={-1}
          aria-hidden="true"
          style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}
        >
          {active && previousTitle && item.hasAutomaticTitle && !doneAnimating ? (
            <AnimateTyping
              oldText={previousTitle}
              newText={title}
              delay={75}
              setDoneAnimating={setDoneAnimating}
            />
          ) : (
            <pre style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {title}
            </pre>
          )}
        </a>
      }
      renderActions={
        isEditing ? undefined : (
          <DocumentListActionMenu
            item={item}
            setNewTitle={setNewTitle}
            onOpenChange={setIsMenuOpen}
          />
        )
      }
    />
  );
});
